import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  IconButton
} from '@mui/material';
import {
  Search,
  Person,
  EventNote,
  MonitorHeart,
  LocalHospital,
  History,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { readPatients, readConsultations, readVitals } from '../services/excelService';

const GlobalSearch = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (query.length > 2) {
      setLoading(true);
      searchAll(query);
    } else {
      setResults([]);
      setLoading(false);
    }
  }, [query]);

  const searchAll = async (searchQuery) => {
    try {
      // Get all data from Excel service
      const patients = readPatients();
      const consultations = readConsultations();
      const vitals = readVitals();

      // Simple search function
      const searchInData = (data, searchTerm) => {
        return data.filter(item => {
          const searchableText = Object.values(item).join(' ').toLowerCase();
          return searchableText.includes(searchTerm.toLowerCase());
        });
      };

      const searchResults = [
        ...searchInData(patients, searchQuery).slice(0, 5).map(item => ({ ...item, type: 'patient' })),
        ...searchInData(consultations, searchQuery).slice(0, 5).map(item => ({ ...item, type: 'consultation' })),
        ...searchInData(vitals, searchQuery).slice(0, 5).map(item => ({ ...item, type: 'vital' }))
      ];

      setResults(searchResults);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    setQuery(event.target.value);
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleResultClick = (result) => {
    let path = '';
    switch (result.type) {
      case 'patient':
        path = `/patients/${result.id}`;
        break;
      case 'consultation':
        // Rediriger vers la page du patient concerné
        path = `/patients/${result.patientId}`;
        break;
      case 'vital':
        // Rediriger vers la page du patient concerné
        path = `/patients/${result.patientId}`;
        break;
      default:
        return;
    }
    
    navigate(path);
    handleClose();
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    setAnchorEl(null);
    if (onClose) onClose();
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'patient':
        return <Person color="primary" />;
      case 'consultation':
        return <EventNote color="secondary" />;
      case 'vital':
        return <MonitorHeart color="error" />;
      default:
        return <Search />;
    }
  };

  const getItemTitle = (item) => {
    switch (item.type) {
      case 'patient':
        return `${item.firstName} ${item.lastName}`;
      case 'consultation':
        return `Consultation - ${item.reason || 'Sans motif'}`;
      case 'vital':
        return `Constantes - ${item.measurement_type || 'Mesure'}`;
      default:
        return item.title || 'Résultat';
    }
  };

  const getItemSubtitle = (item) => {
    switch (item.type) {
      case 'patient':
        return `Né(e) le ${new Date(item.dateOfBirth).toLocaleDateString()}`;
      case 'consultation':
        return new Date(item.consultation_date).toLocaleDateString();
      case 'vital':
        return new Date(item.measurement_date).toLocaleDateString();
      default:
        return item.description || '';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'patient':
        return 'Patient';
      case 'consultation':
        return 'Consultation';
      case 'vital':
        return 'Constantes';
      default:
        return 'Autre';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'patient':
        return 'primary';
      case 'consultation':
        return 'secondary';
      case 'vital':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        ref={inputRef}
        fullWidth
        placeholder="Rechercher patients, consultations, constantes..."
        value={query}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search color="action" />
            </InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setQuery('')}
                edge="end"
              >
                <Clear />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.background.paper,
            },
          },
        }}
      />

      <Popper
        open={open && (query.length > 2 || loading)}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ width: anchorEl?.offsetWidth, zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <Paper
          elevation={8}
          sx={{
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : results.length > 0 ? (
            <List dense>
              {results.map((result, index) => (
                <ListItem key={`${result.type}-${result.id}-${index}`} disablePadding>
                  <ListItemButton
                    onClick={() => handleResultClick(result)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getItemIcon(result.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {getItemTitle(result)}
                          </Typography>
                          <Chip
                            label={getTypeLabel(result.type)}
                            size="small"
                            color={getTypeColor(result.type)}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {getItemSubtitle(result)}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : query.length > 2 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Aucun résultat trouvé pour "{query}"
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Popper>
    </Box>
  );
};

export default GlobalSearch;
